<template>
  <Modal
    ref="modal"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('rowEditModal.itemChip')"
    class="task-modal task-modal--create"
    :close-button="false"
    wide
  >
    <template #header>
      <header class="task-modal__header">
        <div class="task-modal__location">
          <i class="iconoir-plus" aria-hidden="true"></i
          ><strong>{{ $t('taskPanel.newTask') }}</strong
          ><span>{{ table.name }}</span>
        </div>
        <button
          type="button"
          class="task-modal__icon-button"
          :aria-label="$t('taskPanel.close')"
          @click="$refs.modal.hide()"
        >
          <i class="iconoir-cancel" aria-hidden="true"></i>
        </button>
      </header>
    </template>
    <form class="task-modal__create-form" @submit.prevent="create">
      <Error :error="error" />
      <RowEditModalFieldsList
        ref="visibleFieldsList"
        :primary-is-sortable="primaryIsSortable"
        :fields="visibleFields"
        :sortable="sortable"
        :hidden="false"
        :read-only="false"
        :row="row"
        :view="view"
        :table="table"
        :database="database"
        :can-modify-fields="canModifyFields"
        :all-fields-in-table="allFieldsInTable"
        :touched="touched"
        @field-updated="$emit('field-updated', $event)"
        @field-deleted="$emit('field-deleted')"
        @order-fields="$emit('order-fields', $event)"
        @toggle-field-visibility="$emit('toggle-field-visibility', $event)"
        @update="update"
      />
      <RowEditModalFieldsList
        v-if="hiddenFields.length"
        ref="hiddenFieldsList"
        :primary-is-sortable="primaryIsSortable"
        :fields="hiddenFields"
        :sortable="false"
        :hidden="true"
        :read-only="false"
        :row="row"
        :table="table"
        :view="view"
        :database="database"
        :can-modify-fields="canModifyFields"
        :all-fields-in-table="allFieldsInTable"
        :touched="touched"
        @field-updated="$emit('field-updated', $event)"
        @field-deleted="$emit('field-deleted')"
        @toggle-field-visibility="$emit('toggle-field-visibility', $event)"
        @update="update"
      />
      <div class="actions task-modal__footer">
        <div class="align-right">
          <Button
            type="primary"
            size="large"
            :loading="loading"
            :disabled="loading"
          >
            {{ $t('action.create') }}
          </Button>
        </div>
      </div>
    </form>
  </Modal>
</template>

<script>
import modal from '@baserow/modules/core/mixins/modal'
import rowModalFieldWalk from '@baserow/modules/database/mixins/rowModalFieldWalk'
import error from '@baserow/modules/core/mixins/error'
import RowEditModalFieldsList from './RowEditModalFieldsList.vue'
import { getPrimaryOrFirstField } from '@baserow/modules/database/utils/field'

export default {
  name: 'RowCreateModal',
  components: {
    RowEditModalFieldsList,
  },
  mixins: [modal, error, rowModalFieldWalk],
  props: {
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    view: {
      type: [Object, null],
      required: false,
      default: null,
    },
    primaryIsSortable: {
      type: Boolean,
      required: false,
      default: false,
    },
    sortable: {
      type: Boolean,
      required: false,
      default: true,
    },
    canModifyFields: {
      type: Boolean,
      required: false,
      default: true,
    },
    visibleFields: {
      type: Array,
      required: true,
    },
    hiddenFields: {
      type: Array,
      required: false,
      default: () => [],
    },
    showHiddenFields: {
      type: Boolean,
      required: false,
      default: false,
    },
    allFieldsInTable: {
      type: Array,
      required: true,
    },
    presets: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  emits: [
    'created',
    'field-deleted',
    'field-updated',
    'order-fields',
    'toggle-field-visibility',
    'toggle-hidden-fields-visibility',
  ],
  data() {
    return {
      row: {},
      loading: false,
      touched: false,
    }
  },
  computed: {
    allFields() {
      return this.visibleFields.concat(this.hiddenFields)
    },
    heading() {
      const field = getPrimaryOrFirstField(this.visibleFields)

      if (!field) {
        return null
      }

      const name = `field_${field.id}`
      if (Object.prototype.hasOwnProperty.call(this.row, name)) {
        return this.$registry
          .get('field', field.type)
          .toHumanReadableString(field, this.row[name])
      } else {
        return null
      }
    },
  },
  methods: {
    show(defaults = {}, ...args) {
      const row = {}
      const defaultItems = this.view?.default_row_values || []
      const defaultsByFieldId = {}
      for (const item of defaultItems) {
        if (item.enabled && (item.value != null || item.function)) {
          defaultsByFieldId[item.field] = item
        }
      }
      this.allFields.forEach((field) => {
        const name = `field_${field.id}`
        const fieldType = this.$registry.get('field', field._.type.type)
        const dvItem = defaultsByFieldId[field.id]
        const supportedFunctions = fieldType
          .getSupportedDefaultValueFunctions()
          .map((f) => f.name)
        if (this.presets[name] !== undefined) {
          row[name] = this.presets[name]
        } else if (
          dvItem &&
          dvItem.function &&
          supportedFunctions.includes(dvItem.function)
        ) {
          row[name] = fieldType.resolveDefaultValueFunction(
            dvItem.function,
            field
          )
        } else if (
          dvItem &&
          dvItem.value != null &&
          (!dvItem.field_type || dvItem.field_type === field._.type.type)
        ) {
          row[name] = fieldType.parseDefaultRowValue(field, dvItem.value)
        } else {
          row[name] = fieldType.getNewRowValue(field)
        }
      })
      Object.assign(row, defaults)
      this.row = row
      this.touched = false
      return modal.methods.show.call(this, ...args)
    },
    update(event) {
      const name = `field_${event.field.id}`
      this.row[name] = event.value
    },
    create() {
      this.touched = true

      const valid = [this.$refs.visibleFieldsList, this.$refs.hiddenFieldsList]
        .filter(Boolean)
        .every((list) => list.isValid())

      if (!valid) {
        return
      }

      this.loading = true
      this.$emit('created', {
        row: this.row,
        callback: (error) => {
          if (error) {
            this.handleError(error)
          } else {
            this.hide()
          }
          this.loading = false
        },
      })
    },
  },
}
</script>
